// hlcRecord.js — most medzi plochým SQLite riadkom a per-field CRDT modelom (crdtMerge).
// Fáza 2 per-field CRDT. Tu žije BOOTSTRAP starých riadkov bez `_hlc` (commit 2); explode/collapse
// row↔crdtRow a merge wiring pridá commit 4.
//
// `_hlc` tvar (Fable decider verdikt A): { _rev: <rev>, fields: { <col>: "ts:count:node" }, deleted: "ts:count:node"|null }
import { serializeHlc, nextHlc } from './hlcClock';

// Metadata stĺpce — NErazia sa per-field HLC (identita riadku + LWW triplet + samotné `_hlc`).
export const META_FIELDS = new Set([
  'id', 'created_at', 'updated_at', 'rev', 'deleted_at',
  'schema_version', 'obligation_pack_version', '_hlc',
]);

// Sentinel node pre bootstrap — NIKDY lokálny deviceId. Pod každým 8-hex deviceId (string '0'
// < ktorýkoľvek hex), takže pri absolútnej remíze bootstrap PREHRÁVA so skutočným zápisom
// (reálny edit > syntetika). Deterministický naprieč zariadeniami → idempotentný merge.
export const BOOTSTRAP_NODE = '0';

/**
 * Odvodí `_hlc` mapu pre riadok, ktorý ju NEMÁ (starý záznam už na Drive používateľa, alebo
 * riadok od pred-vlna-2 buildu). Čistá funkcia bajtov riadku → dve zariadenia držiace ten istý
 * snapshot vyrazia BAJTOVO IDENTICKÝ `_hlc` (žiadne falošné víťazstvá pri merge).
 *
 * HLC každého poľa: ts = updated_at (epoch ms) → fallback created_at → 0; count = rev;
 * node = sentinel '0'. Každý NOVÝ edit razí `nextHlc()` s ts = aktuálny wall-clock, ktorý je
 * väčší než akýkoľvek starý updated_at → legitímne porazí bootstrap (skutočná zmena vyhráva).
 */
export function hlcBootstrap(row) {
  if (!row || typeof row !== 'object') return { _rev: 0, fields: {}, deleted: null };
  const parsedTs = Date.parse(row.updated_at) || Date.parse(row.created_at) || 0;
  const ts = Number.isFinite(parsedTs) && parsedTs > 0 ? parsedTs : 0;
  const revNum = Number(row.rev);
  const count = Number.isInteger(revNum) && revNum >= 0 ? revNum : 0;
  const stamp = serializeHlc({ ts, count, node: BOOTSTRAP_NODE });

  const fields = {};
  for (const key of Object.keys(row)) {
    if (META_FIELDS.has(key)) continue;
    if (row[key] === undefined) continue; // neexistujúce pole nedostane pečiatku
    fields[key] = stamp;
  }
  return { _rev: count, fields, deleted: row.deleted_at ? stamp : null };
}

/** Content polia objektu (mínus metadata/LWW triplet/_hlc) s definovanou hodnotou. */
export function contentFields(obj) {
  if (!obj) return [];
  return Object.keys(obj).filter((k) => !META_FIELDS.has(k) && obj[k] !== undefined);
}

/**
 * Vyrazí/aktualizuje `_hlc` mapu pre zápis (Fáza 2 commit 3, mint). Jeden HLC pre celú mutáciu
 * (všetky zmenené polia dostanú tú istú pečiatku = jeden logický „čas" zápisu). Zachová pečiatky
 * NEzmenených polí z `prevHlc`. `_rev` = nový rev → merge (commit 4) detekuje stale mapu cez
 * `_hlc._rev !== rev`. Vyžaduje setHlcNode() (initV2 / test).
 * @returns {{_rev:number, fields:Object, deleted:string|null}}
 */
export function stampRecordHlc({ prevHlc = null, changed = [], rev, deleted = false, wallMs } = {}) {
  const stamp = serializeHlc(nextHlc(wallMs));
  const fields = { ...(prevHlc && prevHlc.fields ? prevHlc.fields : {}) };
  for (const name of changed) fields[name] = stamp;
  const tombstone = deleted ? stamp : (prevHlc ? prevHlc.deleted ?? null : null);
  return { _rev: Number(rev) || 0, fields, deleted: tombstone };
}

/** Doplní `_hlc` do NOVÉHO záznamu (add): všetky content polia dostanú pečiatku. Vracia ten istý objekt. */
export function stampAdd(record) {
  if (!record) return record;
  record._hlc = stampRecordHlc({
    prevHlc: null,
    changed: contentFields(record),
    rev: Number(record.rev) || 1,
    deleted: Boolean(record.deleted_at),
  });
  return record;
}

/**
 * Doplní `_hlc` do PATCHU (update): opečiatkuje len zmenené content polia, zachová pečiatky ostatných
 * z existujúceho záznamu. Ak starý záznam `_hlc` nemá, bootstrapne ho (nezmenené polia dostanú
 * syntetickú pečiatku z updated_at/rev), aby collapse mal kompletnú mapu. Vracia ten istý patch.
 */
export function stampUpdate(existingRow, patch) {
  if (!patch) return patch;
  const prevHlc = existingRow && existingRow._hlc ? existingRow._hlc : (existingRow ? hlcBootstrap(existingRow) : null);
  const deleted = patch.deleted_at !== undefined
    ? Boolean(patch.deleted_at)
    : Boolean(existingRow && existingRow.deleted_at);

  let changed = contentFields(patch);
  // Resurrect (Sol #3): záznam bol zmazaný a patch ho oživuje (deleted_at → null), ale nemení žiadne
  // content pole → bez razenia by tombstone ostal najnovší a merge by ho znovu zabil. CRDT resurrect =
  // dotkni sa VŠETKÝCH existujúcich polí novým HLC (hodnota rovnaká), aby najnovší field prevýšil
  // tombstone → isAlive resurrektuje. `_hlc.deleted` môže ostať (field > tombstone rozhodne).
  const isResurrect = existingRow && existingRow.deleted_at && patch.deleted_at === null;
  if (isResurrect && prevHlc && prevHlc.fields) {
    changed = [...new Set([...changed, ...Object.keys(prevHlc.fields)])];
  }

  patch._hlc = stampRecordHlc({
    prevHlc,
    changed,
    rev: Number(patch.rev) || (Number(existingRow && existingRow.rev) || 1),
    deleted,
  });
  return patch;
}
